import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { OfferPreviewItem, OfferPreviewRequest } from '../../types/offers';
import VariantPriceTable from './VariantPriceTable';

interface OfferVariantPricesProps {
  offerId: string;
  version: number;
  token: string;
  /**
   * #997 (admin edit): the draft rule from the edit form. Omitted → the offer's own rule, fetched at
   * once. Present → that rule, debounced; `null` → the draft cannot be priced yet (no discount or no service).
   */
  draft?: OfferPreviewRequest | null;
  /** Debounce for draft changes, like the partner `useOfferVariantPreview`. */
  delayMs?: number;
}

/**
 * Decision "4 preview" — the offer's rule priced per bookable variant of every covered service, so the
 * approver sees what customers will really pay (Spec 073: each service's own discount, applied to all its variants).
 */
export default function OfferVariantPrices({ offerId, version, token, draft, delayMs = 400 }: OfferVariantPricesProps) {
  const [items, setItems] = useState<OfferPreviewItem[] | null>(null);
  const [error, setError] = useState(false);
  const isDraft = draft !== undefined;
  const draftKey = isDraft ? JSON.stringify(draft) : '';

  useEffect(() => {
    let cancelled = false;
    setError(false);
    if (!isDraft) {
      api.previewOffer(offerId, token)
        .then((res) => { if (!cancelled) setItems(res?.items ?? []); })
        .catch(() => { if (!cancelled) setError(true); });
      return () => { cancelled = true; };
    }
    if (draft == null) {
      setItems(null);
      return () => { cancelled = true; };
    }
    setItems(null);
    const timer = setTimeout(() => {
      api.previewOffer(offerId, token, draft)
        .then((res) => { if (!cancelled) setItems(res?.items ?? []); })
        .catch(() => { if (!cancelled) setError(true); });
    }, delayMs);
    return () => { cancelled = true; clearTimeout(timer); };
    // `draftKey` captures every field of the draft the request sends.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, version, token, isDraft, draftKey, delayMs]);

  const skipped = items?.reduce((n, i) => n + i.skippedCount, 0) ?? 0;
  const waitingForRule = isDraft && draft == null;

  return (
    <div className="admin-card" data-testid="offer-variant-prices">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Prices per variant</h3>
      </div>
      <p className="admin-text-muted" style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem' }}>
        {isDraft
          ? 'Your edited discounts, priced the way checkout prices them. Before is today\'s list price, after is what customers will pay.'
          : "Each service's discount applies to all of its variants at checkout. These are the prices customers will pay."}
      </p>
      {skipped > 0 && (
        <div className="admin-info-banner warning" role="status" style={{ marginBottom: '0.5rem' }}>
          {skipped === 1 ? '1 variant gets' : `${skipped} variants get`} no discount under this rule.
        </div>
      )}
      {error && <div className="admin-info-banner danger" role="alert">Could not load the prices per variant.</div>}
      {waitingForRule && <div className="admin-text-muted" role="status">Set a discount and pick a service to see prices.</div>}
      {!error && !waitingForRule && items == null && <div className="admin-text-muted" role="status">Loading prices…</div>}
      {items?.map((item) => <VariantPriceTable key={item.offeringId} item={item} />)}
    </div>
  );
}
